import React, { useMemo } from 'react';
import { FormBuilderContainerProps, FormBuilderViewProps } from './formBuilderProps';
import { ReportingContainerBaseConfig } from '../../../../base';
import { FormBuilderController, FormBuilderLoader } from './formBuilderInterface';
import { useForm } from 'react-hook-form';
import { buildFormStateValues, DocumentFormFormValues } from './state';
import { FormInterviewMetadata } from './formBuilderModel';
import { TONE_OPTIONS } from './formBuilderConstant';
import { buildFormPayload } from './formBuilderFactory';

export function createFormBuilderContainer(
    config: ReportingContainerBaseConfig,
    loader: FormBuilderLoader,
    controller: FormBuilderController,
    View: React.FC<FormBuilderViewProps>
): React.FC<FormBuilderContainerProps> {
    const {
        kernel: {
            infra: {
                toaster: { useToast },
            },
        },
        context: { useContext },
        repository: { research },
    } = config;

    return ({ ...containerProps }) => {
        const context = useContext();
        const data = loader.useLoad(context, {
            document: {
                id: containerProps.document.id,
            },
            form: {
                id: containerProps.form.id,
            },
        });

        const toast = useToast();
        const mutation = {
            form: {
                update: research.form.useUpdate(context),
            },
        };

        const form = useForm<DocumentFormFormValues>({
            defaultValues: useMemo(
                () =>
                    buildFormStateValues({
                        form: data.form,
                        transcript: {
                            // TODO keep in sync with form payloads
                            key: 'transcript',
                        },
                    }),
                [data.form]
            ),
        });

        const formValues = form.watch();
        const props = controller.useProps(context, data, {
            values: formValues,
            field: {
                tone: {
                    options: TONE_OPTIONS,
                },
            },
            form: {
                id: 'form',
                form,
                async onSubmit(values) {
                    console.log('onSubmit', values);

                    try {
                        const payload = buildFormPayload({
                            values,
                            current: data.form,
                        });
                        await mutation.form.update.mutateAsync({
                            formId: data.form.id,
                            payload,
                        });
                        toast({
                            kind: 'success',
                            description: 'Your form has been successfully updated.',
                            durationMs: 3000,
                        });
                        // Reset form state after successful save
                        form.reset(values);
                    } catch (error) {
                        console.error('Form update error:', error);
                        toast({
                            kind: 'error',
                            description: 'Failed to update the form. Please try again.',
                            durationMs: 5000,
                        });
                    }
                },
            },
        });
        return <View {...props} />;
    };
}
